import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';

class PageTransitions {
  constructor(initPageModules) {
    const swup = new Swup({
      containers: ['#page-header', '#page-content'],
      animationSelector: '#page-content',
      plugins: [
        new SwupHeadPlugin({ persistAssets: true }),
        new SwupScrollPlugin({ animateScroll: false }),
      ],
    });

    swup.hooks.on('content:replace', (visit) => {
      const incomingDoc = visit.to.document;
      if (!incomingDoc) return;
      const swupClasses = ['is-animating', 'is-leaving', 'is-rendering', 'is-changing', 'is-popstate'];
      const active = swupClasses.filter(cls => document.documentElement.classList.contains(cls));
      // Preserve vanilla-cookieconsent classes (show--consent, cc--elegant-black, etc.)
      const ccClasses = [...document.documentElement.classList].filter(
        cls => cls.startsWith('cc--') || cls.startsWith('show--')
      );
      document.documentElement.className = incomingDoc.documentElement.className;
      active.forEach(cls => document.documentElement.classList.add(cls));
      ccClasses.forEach(cls => document.documentElement.classList.add(cls));

      this.syncPersistentSubNavigation(incomingDoc);
    });

    this.updateMastheadActiveLinks();

    swup.hooks.on('page:view', () => {
      const currentPath = this.normalisePath(window.location.pathname);

      this.updateMastheadActiveLinks();
      this.updateActiveLinks('.c-navigation a', currentPath, { startsWith: false });
      this.decodeCFEmails();
      this.trackPageView();

      initPageModules();
    });
  }

  normalisePath(path) {
    if (!path) return '/';
    const trimmed = path.replace(/\/+$/, '');
    return trimmed || '/';
  }

  updateActiveLinks(selector, currentPath, { startsWith = false } = {}) {
    document.querySelectorAll(selector).forEach(link => {
      const linkPath = this.normalisePath(new URL(link.href, window.location.origin).pathname);
      const isActive = startsWith
        ? this.isPathMatch(currentPath, linkPath)
        : currentPath === linkPath;

      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  isPathMatch(currentPath, linkPath) {
    if (currentPath === linkPath) return true;
    if (linkPath === '/') return currentPath === '/';
    return currentPath.startsWith(`${linkPath}/`);
  }

  getFirstPathSegment(path) {
    return this.normalisePath(path).split('/').filter(Boolean)[0] || '';
  }

  updateMastheadActiveLinks() {
    const currentPath = this.normalisePath(window.location.pathname);
    const currentSegment = this.getFirstPathSegment(currentPath);

    document.querySelectorAll('.c-masthead__nav a').forEach(link => {
      const linkPath = this.normalisePath(new URL(link.href, window.location.origin).pathname);
      const linkSegment = this.getFirstPathSegment(linkPath);
      const isSegmentMatch = currentSegment && linkSegment && currentSegment === linkSegment;
      const isActive = isSegmentMatch || this.isPathMatch(currentPath, linkPath);

      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  trackPageView() {
    // gtag's automatic page_view only fires on the initial hard load (see
    // send_page_view in CookieConsent.js). Swup swaps content without a
    // reload, so each transition needs its own manual page_view event.
    if (typeof gtag === 'function') {
      gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
      });
    }
  }

  decodeCFEmails() {
    document.querySelectorAll('[data-cfemail]').forEach(el => {
      const encoded = el.getAttribute('data-cfemail');
      const key = parseInt(encoded.substr(0, 2), 16);
      let decoded = '';
      for (let i = 2; i < encoded.length; i += 2) {
        decoded += String.fromCharCode(parseInt(encoded.substr(i, 2), 16) ^ key);
      }
      try { decoded = decodeURIComponent(escape(decoded)); } catch (e) {}
      el.textContent = decoded;
      const link = el.closest('a[href*="/cdn-cgi/l/email-protection"]');
      if (link) link.href = 'mailto:' + decoded;
    });
  }

  syncPersistentSubNavigation(incomingDoc) {
    const currentSubNav = document.querySelector('.c-navigation');
    const incomingSubNav = incomingDoc.querySelector('.c-navigation');

    if (!incomingSubNav) {
      if (currentSubNav) {
        currentSubNav.remove();
      }
      return;
    }

    const nextSubNav = incomingSubNav.cloneNode(true);

    if (currentSubNav) {
      currentSubNav.replaceWith(nextSubNav);
      return;
    }

    const pageHeader = document.querySelector('#page-header');
    if (pageHeader) {
      pageHeader.insertAdjacentElement('afterend', nextSubNav);
    }
  }
}

export default PageTransitions;
